import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import styled, { keyframes } from "styled-components";
import uuid from "uuid";
import ReactTooltip from "react-tooltip";
import AppSettings from "../../settings";
import LoadingIndicator from "../LoadingIndicator";
import { Button, ButtonIcon } from "../../styled-components/";
import { actions as assetActions } from "../../actions/asset";
import { generateShortcutText } from "../../util/keyShortcutUtils";
import { actions as snackbarActions } from "../../actions/snackbar";
import SnackbarButtonTemplates from "../../constants/snackbarButtonTemplates";
import ViewPortSize from "../../constants/viewPortSize";
import { makeGetConfigValue } from "../../selectors/config";
import { actions as keyShortcutsActions } from "../../actions/keyShortcuts";
import localize from "../../util/localizeUtils";

const Mousetrap = window.Mousetrap;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const PreviewImage = styled.img`
  -webkit-animation: ${fadeIn} 0.5s;
  -moz-animation: ${fadeIn} 0.5s;
  -ms-animation: ${fadeIn} 0.5s;
  -o-animation: ${fadeIn} 0.5s;
  animation: ${fadeIn} 0.5s;
  width: 100%;
  max-height: 100%;
  object-fit: contain;
  background-color: black;
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: ${props => props.backgroundColor || props.theme.background};
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  align-self: center;
  height: 3rem;
  border-radius: 0.25rem;
`;

const DragMessage = styled.div`
  opacity: 0;
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
  border: 1px solid transparent;
  font-size: 15px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  width: 100%;
  border-radius: 10px;

  &.enabled {
    opacity: 1;
    -webkit-transition: opacity 250ms ease-in;
    -moz-transition: opacity 250ms ease-in;
    -o-transition: opacity 250ms ease-in;
    -ms-transition: opacity 250ms ease-in;
    transition: opacity 250ms ease-in;
  }
`;

const Icon = styled(ButtonIcon)`
  margin: 0;
`;

const PreviewText = styled.div`
  margin-left: 0.25rem;
`;

class AssetProperties extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      backgroundColor: null
    };
  }

  componentDidMount() {
    this.bindShortcuts();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.previewError && nextProps.previewError) {
      const id = uuid.v4();
      this.props.showSnack(id, {
        content: localize("previewError"),
        timeout: 7000,
        buttonTemplate: SnackbarButtonTemplates.DISMISS
      });
    }
  }

  componentWillUnmount() {
    this.unbindShortcuts();
  }

  getPreviewImage = () => {
    const { thumbnailUrl, previewImg } = this.props;
    const querySeparator =
      thumbnailUrl && thumbnailUrl.includes("?") ? "&" : "?";

    if (previewImg) {
      return `data:image/png;base64,${escape(previewImg)}`;
    } else if (thumbnailUrl && thumbnailUrl.includes("http://")) {
      return `${thumbnailUrl}${querySeparator}${new Date().getTime()}`;
    }
    return `${
      AppSettings.camioServer
    }${thumbnailUrl}${querySeparator}${new Date().getTime()}`;
  };

  bindShortcuts = () => {
    const { preview } = this.props.shortcuts;
    Mousetrap.bindGlobal(preview, () => {
      if (!this.props.generatingPreview) {
        this.props.generatePreview();
      }
    });
  };

  unbindShortcuts = () => {
    const { preview } = this.props.shortcuts;
    Mousetrap.unbind(preview);
  };

  dragStart = event => {
    // The three lines below are meant to clear the drag and drop payload.
    event.dataTransfer.setData("text/plain", this.props.insertedMosXml);
    event.dataTransfer.setData("text/html", "");
    event.dataTransfer.setData("text/uri-list", "");
  };

  openPreview = () => {
    const previewWindow = window.open("", "_blank");
    // Chrome does not support opening windows with data urls,
    // so we inject an img tag into the new document.
    previewWindow.document.write(
      `<img style="max-width: 100%; max-height: 100%;" src=${this.getPreviewImage()} />`
    );
  };

  render() {
    const {
      generatingPreview,
      generatePreview,
      isModifierActive,
      shortcuts,
      viewPortSize,
      insertedMosXml
    } = this.props;
    return (
      <Container backgroundColor={this.state.backgroundColor}>
        <ImageContainer>
          <PreviewImage
            src={this.getPreviewImage()}
            onDragStart={this.dragStart}
            onDragEnd={this.dragEnd}
            onDoubleClick={this.openPreview}
            ref={ref => (this.preview = ref)}
            draggable={!!insertedMosXml}
            data-tip
            data-for="assetPreview"
          />
        </ImageContainer>
        <MessageContainer size={viewPortSize}>
          <DragMessage className={!!insertedMosXml ? "enabled" : ""}>
            {localize("dragReady")}
          </DragMessage>
        </MessageContainer>
        <ReactTooltip id="assetPreview" place="left" type="dark" effect="solid">
          <span>{localize("enlargeTooltip")}</span>
        </ReactTooltip>
        <LoadingIndicator visible={generatingPreview} />
      </Container>
    );
  }
}

AssetProperties.defaultProps = {
  thumbnailUrl: "",
  previewImg: "",
  previewError: null,
  insertedMosXml: null
};

AssetProperties.propTypes = {
  editing: PropTypes.bool.isRequired,
  thumbnailUrl: PropTypes.string,
  previewImg: PropTypes.string,
  generatingPreview: PropTypes.bool.isRequired,
  isMosObject: PropTypes.bool.isRequired,
  createNewMosObject: PropTypes.func.isRequired,
  createMosObjectFromExisting: PropTypes.func.isRequired,
  replaceMosObjectWithNew: PropTypes.func.isRequired,
  updateMosObject: PropTypes.func.isRequired,
  previewError: PropTypes.string,
  showSnack: PropTypes.func.isRequired,
  generatePreview: PropTypes.func.isRequired,
  shortcuts: PropTypes.shape({
    preview: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  isModifierActive: PropTypes.bool.isRequired,
  viewPortSize: PropTypes.number.isRequired,
  forceEditNew: PropTypes.bool.isRequired,
  setModifierKeyState: PropTypes.func.isRequired,
  swapping: PropTypes.bool.isRequired,
  insertedMosXml: PropTypes.string,
  inserting: PropTypes.bool.isRequired
};
const makeMapStateToProps = () => {
  const getConfigValue = makeGetConfigValue();
  const mapStateToProps = state => ({
    editing: state.asset.editing,
    thumbnailUrl: state.asset.asset.thumbnailUrl,
    previewImg: state.asset.asset.previewImg,
    generatingPreview: state.asset.generatingPreview,
    isMosObject: state.asset.isMosObject,
    previewError: state.asset.previewError,
    shortcuts: state.keyShortcuts.shortcuts,
    isModifierActive: state.keyShortcuts.isModifierActive,
    viewPortSize: state.app.viewPortSize,
    forceEditNew: getConfigValue(state, "mos", "defaultEditNew", false),
    swapping: state.asset.swapping,
    insertedMosXml: state.asset.insertedMosXml,
    inserting: state.asset.inserting
  });
  return mapStateToProps;
};
const mapActionCreators = {
  createNewMosObject: assetActions.createNewMosObject,
  createMosObjectFromExisting: assetActions.createMosObjectFromExisting,
  replaceMosObjectWithNew: assetActions.replaceMosObjectWithNew,
  updateMosObject: assetActions.updateMosObject,
  showSnack: snackbarActions.showSnack,
  generatePreview: assetActions.generatePreview,
  setModifierKeyState: keyShortcutsActions.setModifierKeyState
};

export default connect(makeMapStateToProps, mapActionCreators)(AssetProperties);



// WEBPACK FOOTER //
// ./app/components/Editor/AssetPreview.js